import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Save, RotateCcw, GripVertical, Eye, Settings,
  FileText, Receipt, Truck, FileMinus, Package, CreditCard,
  ClipboardList, DollarSign, Banknote, ScanLine, PackagePlus,
  BarChart3, ClipboardCheck, Users, Building2, TrendingUp,
  Clock, Briefcase, ShieldCheck, Inbox, AlertTriangle, AlertCircle, Circle,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const ICON_MAP = {
  "file-text": FileText,
  "receipt": Receipt,
  "truck": Truck,
  "file-minus": FileMinus,
  "package": Package,
  "credit-card": CreditCard,
  "clipboard-list": ClipboardList,
  "dollar-sign": DollarSign,
  "banknote": Banknote,
  "scan-line": ScanLine,
  "package-plus": PackagePlus,
  "bar-chart-3": BarChart3,
  "clipboard-check": ClipboardCheck,
  "users": Users,
  "building-2": Building2,
  "trending-up": TrendingUp,
  "clock": Clock,
  "briefcase": Briefcase,
  "shield-check": ShieldCheck,
  "inbox": Inbox,
  "alert-triangle": AlertTriangle,
  "alert-circle": AlertCircle,
  "circle": Circle,
};

function getIcon(iconName) {
  return ICON_MAP[iconName] || Circle;
}

function SortableSlot({ slot, index, actions, onUpdate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slot.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : "auto", opacity: isDragging ? 0.5 : 1 };

  const action = slot.action_type ? actions[slot.action_type] : null;
  const displayLabel = slot.custom_label || (action ? action.label : "Select Action");
  const IconComp = action ? getIcon(action.icon) : Circle;

  const grouped = useMemo(() => {
    const cats = {};
    Object.entries(actions).forEach(([key, a]) => {
      if (!cats[a.category]) cats[a.category] = [];
      cats[a.category].push({ key, ...a });
    });
    return cats;
  }, [actions]);

  return (
    <div ref={setNodeRef} style={style} className={`${!slot.enabled ? "opacity-60" : ""}`}>
      <Card className={`border ${slot.enabled ? "border-gray-200 hover:border-[#E63946]" : "border-gray-100 bg-gray-50/50"} rounded-sm shadow-sm transition-all`}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-testid={`drag-handle-${index}`}>
                <GripVertical className="w-5 h-5" />
              </button>
              <span className="text-sm font-semibold text-gray-700">Button {index + 1}</span>
              {action && <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{action.category}</span>}
            </div>
            <Switch data-testid={`toggle-btn-${index}`} checked={slot.enabled} onCheckedChange={(v) => onUpdate({ enabled: v })} />
          </div>

          <div className={`space-y-4 ${!slot.enabled ? "pointer-events-none opacity-50" : ""}`}>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Action</label>
              <Select value={slot.action_type || ""} onValueChange={(v) => onUpdate({ action_type: v, custom_label: "" })}>
                <SelectTrigger data-testid={`action-select-${index}`} className="rounded-sm"><SelectValue placeholder="Select action..." /></SelectTrigger>
                <SelectContent>
                  {Object.entries(grouped).map(([cat, items]) => (
                    <div key={cat}>
                      <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400">{cat}</div>
                      {items.map((item) => {
                        const IC = getIcon(item.icon);
                        return <SelectItem key={item.key} value={item.key}><span className="flex items-center gap-2"><IC className="w-3.5 h-3.5" /> {item.label}</span></SelectItem>;
                      })}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              {action && <p className="text-xs text-gray-400 mt-1.5">{action.description}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Custom Label</label>
              <Input data-testid={`label-input-${index}`} value={slot.custom_label || ""} onChange={(e) => onUpdate({ custom_label: e.target.value })} placeholder={action ? action.label : "Label..."} className="rounded-sm" />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">Color</label>
              <div className="flex items-center gap-3">
                <input data-testid={`color-input-${index}`} type="color" value={slot.color} onChange={(e) => onUpdate({ color: e.target.value })} className="w-10 h-10 rounded cursor-pointer border border-gray-200" />
                <Input value={slot.color} onChange={(e) => onUpdate({ color: e.target.value })} className="rounded-sm w-28 font-mono text-sm" />
              </div>
            </div>

            {action && (
              <div className="pt-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Preview</label>
                <div data-testid={`preview-btn-${index}`} className="flex flex-col items-center justify-center p-5 rounded-lg text-white min-h-[100px] shadow-sm transition-transform hover:-translate-y-0.5" style={{ backgroundColor: slot.color }}>
                  <IconComp className="w-8 h-8 mb-2" strokeWidth={1.5} />
                  <span className="text-sm font-semibold text-center leading-tight">{displayLabel}</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function DashboardConfigPage() {
  const [slots, setSlots] = useState([]);
  const [actions, setActions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    api.get("/dashboard-buttons").then((r) => {
      const { buttons, available_actions } = r.data;
      setActions(available_actions);
      const loaded = [];
      for (let i = 0; i < 8; i++) {
        const btn = buttons[i] || { position: i + 1, enabled: false, action_type: "", custom_label: "", color: "#E63946" };
        loaded.push({ ...btn, id: `slot-${i}` });
      }
      setSlots(loaded);
    }).catch(() => toast.error("Failed to load dashboard config")).finally(() => setLoading(false));
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSlots((prev) => {
      const oldIdx = prev.findIndex((s) => s.id === active.id);
      const newIdx = prev.findIndex((s) => s.id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const updateSlot = (idx, updates) => {
    setSlots((prev) => prev.map((s, i) => (i === idx ? { ...s, ...updates } : s)));
  };

  const handleSave = async () => {
    const enabledSlots = slots.filter((s) => s.enabled);
    for (const s of enabledSlots) {
      if (!s.action_type) {
        toast.error(`An enabled button has no action selected`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = slots.map((s, i) => ({
        position: i + 1,
        enabled: s.enabled,
        action_type: s.action_type || "",
        custom_label: s.custom_label || null,
        color: s.color || "#E63946",
      }));
      await api.post("/dashboard-buttons", { buttons: payload });
      toast.success("Dashboard configuration saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset to default configuration? This will overwrite your current settings.")) return;
    try {
      const r = await api.post("/dashboard-buttons/reset");
      const buttons = r.data.buttons || [];
      const loaded = [];
      for (let i = 0; i < 8; i++) {
        const btn = buttons[i] || { position: i + 1, enabled: false, action_type: "", custom_label: "", color: "#E63946" };
        loaded.push({ ...btn, id: `slot-${i}` });
      }
      setSlots(loaded);
      toast.success("Reset to default configuration");
    } catch {
      toast.error("Failed to reset");
    }
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-sm" />;

  return (
    <div className="animate-fade-in max-w-5xl" data-testid="dashboard-config-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1F2937] tracking-tight">Dashboard Quick Actions</h1>
          <p className="text-sm text-gray-500 mt-1">Configure up to 8 quick action buttons for your dashboard</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="preview-dashboard-btn" variant="outline" onClick={() => navigate("/dashboard")} className="rounded-sm border-gray-300">
            <Eye className="w-4 h-4 mr-1.5" /> Preview
          </Button>
          <Button data-testid="reset-defaults-btn" variant="outline" onClick={handleReset} className="rounded-sm border-gray-300">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
          </Button>
          <Button data-testid="save-config-btn" onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-sm p-4 mb-6">
        <p className="text-sm text-blue-800"><strong>Tip:</strong> Drag and drop button slots to reorder them. Only enabled buttons will appear on your dashboard.</p>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={slots.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {slots.map((slot, idx) => (
              <SortableSlot key={slot.id} slot={slot} index={idx} actions={actions} onUpdate={(u) => updateSlot(idx, u)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
